// useRenderProgress — opens an EventSource against /api/v1/web/renders/stream
// and exposes the latest per-job progress map. Components reading this hook
// merge live data on top of the polled list, so the queue UI animates in
// real-time without re-fetching.
//
// EventSource auth: the voc_access cookie rides along via withCredentials:true.
// studio.vocaler.ai → api.vocaler.ai is same-site so SameSite=Lax is fine.
//
// Reconnect behaviour: EventSource auto-reconnects on connection loss with the
// browser's built-in policy. We additionally listen for window 'focus' to
// kick a manual restart if the tab was idle for a while.

import { useEffect, useRef, useState } from "react";
import { env } from "../config/env";
import { services } from "../services";
import type { RenderProgressFrame } from "../types";

export type ProgressMap = Record<string, RenderProgressFrame>;

export function useRenderProgress(enabled = true): ProgressMap {
  const [map, setMap] = useState<ProgressMap>({});
  const esRef = useRef<EventSource | null>(null);

  useEffect(() => {
    if (!enabled || env.useMocks) return;
    if (typeof window === "undefined" || typeof window.EventSource === "undefined") return;
    const url = services.renders.streamUrl();
    if (!url) return;
    const es = new EventSource(url, { withCredentials: true });
    esRef.current = es;
    es.addEventListener("progress", (ev) => {
      try {
        const frame = JSON.parse((ev as MessageEvent).data) as RenderProgressFrame;
        if (frame && frame.jobId) {
          setMap((prev) => ({ ...prev, [frame.jobId]: frame }));
        }
      } catch {
        /* ignore malformed frame */
      }
    });
    es.addEventListener("idle", () => {
      // Server closed because no active jobs; drop the local map so the UI
      // shows zeros instead of stale progress.
      setMap({});
    });
    es.onerror = () => {
      // Let EventSource auto-reconnect; nothing to do here.
    };
    function onFocus() {
      if (esRef.current && esRef.current.readyState === EventSource.CLOSED) {
        esRef.current = new EventSource(url, { withCredentials: true });
      }
    }
    window.addEventListener("focus", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
      es.close();
      esRef.current = null;
    };
  }, [enabled]);

  return map;
}
